import { Application, Container, Text, TextStyle } from 'pixi.js';
import { CANVAS_WIDTH } from './constants';
import { validateAndUpdatePersistedStructure } from './defaultSettings';
import { toggleFullscreen } from './utils';
import './styles.css';



import SocketClient from './socket-client';
import GameStateManager from './gameStateManager';
import ViewportManager from './viewportManager';
import RenderManager from './renderManager';
import PlatformManager from './platformManager';
import ParticleEffect from './particleEffect';
import BallManager from './ballManager';
import InputManager from './inputManager';
import UIManager from './uiManager';
import PlayerList from './playerList';
import LevelsList from './levelsList';
import WindowsManager from './windowsManager';
import Chat from './chat';
import { initializeLoadingScreen } from './LoadingScreen';
import SoundManager from './sound';
import { GameMode, RoomState, GameType, StrikeType, GoalType } from '../shared/sharedTypes';
import { DiscordActivityManager } from './discordActivity';
import CampaignManager from './CampaignManager';
import CampaignUI from './CampaignUI';
import CampaignLevelCompleteModal from './CampaignLevelCompleteModal';
import { createPlatformParams } from './platforms/platformParams';
import { DailyChallenge } from './utils/DailyChallenge';
import NewSettingsUI from './ui/NewSettingsUI';
import RecoveryUI from './ui/RecoveryUI';

const show_fps = false;

class Game {
    private app: Application;
    private gameWorld: Container;
    private matterWorker: Worker;

    public socketClient: SocketClient;
    private gameStateManager: GameStateManager;
    private soundManager: SoundManager;
    private viewportManager: ViewportManager;
    private renderManager: RenderManager;
    private platformManager: PlatformManager;
    private ballManager: BallManager;
    private inputManager: InputManager;
    private uiManager: UIManager;
    private playerList: PlayerList;
    private chat: Chat;
    private discordActivityManager: DiscordActivityManager;
    private campaignManager: CampaignManager;
    private campaignUI: CampaignUI;
    private campaignLevelCompleteModal: CampaignLevelCompleteModal;
    private recoveryUI: RecoveryUI;

    private fpsText: Text;
    private frameCount: number = 0;
    private lastTime: number = 0;
    private fps: number = 0;
    private physicsFrameCount: number = 0;
    private lastPhysicsTime: number = 0;
    private physicsFps: number = 0;
    bootstrapRoomState: RoomState;

    constructor() { }

    public async init() {
        this.app = new Application();
        this.gameWorld = new Container();

        // Create and set up the Web Worker
        this.matterWorker = new Worker('/matterWorker.js');
        this.matterWorker.onmessage = this.handleWorkerMessage.bind(this);
        this.matterWorker.postMessage({ type: 'init' });

        await this.app.init({
            width: CANVAS_WIDTH,
            height: window.innerHeight,
            resolution: window.devicePixelRatio || 1,
            autoDensity: true,
            backgroundColor: 0x1099bb,
            antialias: true, // Enable anti-aliasing for smooth edges
            preference: 'webgl', // Prefer WebGL for better quality
        });

        // Configure canvas for crisp rendering
        const canvas = this.app.canvas as HTMLCanvasElement;
        if (canvas) {
            // Enable high-quality image smoothing for 2D context fallback
            const ctx = canvas.getContext('2d');
            if (ctx && 'imageSmoothingEnabled' in ctx) {
                ctx.imageSmoothingEnabled = true;
                if ('imageSmoothingQuality' in ctx) {
                    (ctx as any).imageSmoothingQuality = 'high';
                }
            }
        }
        document.getElementById('game-container')!.appendChild(this.app.canvas);
        document.getElementById('game-container')!.classList.remove('hidden');

        // Hide old settings button - using new design system
        const oldSettingsToggle = document.getElementById('settings-toggle');
        if (oldSettingsToggle) {
            oldSettingsToggle.style.display = 'none';
        }
        const oldSettingsPopup = document.getElementById('settings-popup');
        if (oldSettingsPopup) {
            oldSettingsPopup.style.display = 'none';
        }
        this.app.stage.addChild(this.gameWorld);

        // Initialize the appropriate client based on the environment variable
        this.socketClient = new SocketClient(this.bootStrapCallback.bind(this));

        // Check if we're in campaign mode and create a minimal room state for initialization
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('mode') === 'campaign') {
            const settings = JSON.parse(localStorage.getItem('golfUpSettings') || '{}');
            if (settings.roomSettings?.gameType === 'campaign' && !settings.roomSettings.softMultiplayer) {
                // Wait for socket to connect, then create minimal room state
                this.socketClient.socket.once('connect', () => {
                    const playerId = this.socketClient.socket.id || 'local-player';
                    const minimalRoomState: RoomState = {
                        roomSettings: {
                            levelId: '',
                            height: 5400,
                            gameType: GameType.Campaign,
                            gameMode: GameMode.GolfUP,
                            strikeType: StrikeType.Golf,
                            goalType: GoalType.FinishLine,
                            timeRules: 'countdown'
                        },
                        players: {
                            [playerId]: {
                                id: playerId,
                                name: this.socketClient.name,
                                ballColor: this.socketClient.ballColor,
                                position: { x: 0, y: 0 },
                                inMotion: false,
                                highestHeight: 0,
                                hasHitTheGoal: false,
                                joinedAt: Date.now(),
                                scoring: {
                                    score: [0],
                                    coins: [0],
                                    clicks: [0],
                                    hx: [0],
                                    ttg: [0]
                                }
                            }
                        },
                        round: 0,
                        totalRounds: 1,
                        roundDuration: 0,
                        timeUntilNextRound: 0,
                        gameStartTime: Date.now(),
                        roundStartTime: Date.now()
                    };
                    // Bootstrap with minimal state so campaign UI can be shown
                    if (!this.bootstrapRoomState) {
                        this.bootStrapCallback(minimalRoomState);
                    }
                });
            }
        }
    }

    private handleWorkerMessage(event: MessageEvent) {
        if (event.data.type === 'engineUpdated') {
            this.updatePhysicsFPS();
        }
    }

    async bootStrapCallback(initialState: RoomState) {
        console.log('[LOADING] bootStrapCallback called with initialState');
        this.bootstrapRoomState = initialState;
        await this.initializeGameComponents();
        console.log('[LOADING] initializeGameComponents completed');
        show_fps && this.setupFPSDisplay();
        this.setupFullscreenHandling();
    }

    private async initializeGameComponents() {
        console.log('[LOADING] initializeGameComponents started');
        this.soundManager = new SoundManager();
        /*
        this.discordActivityManager = new DiscordActivityManager();
        // Don't await here to avoid blocking game load if Discord is slow or not present
        this.discordActivityManager.initialize();
        */

        this.gameStateManager = new GameStateManager(this.bootstrapRoomState, this.socketClient, this.soundManager);
        this.socketClient.gameStateManager = this.gameStateManager;
        this.viewportManager = new ViewportManager(this.app, this.gameWorld);
        this.renderManager = new RenderManager(this.app, this.gameWorld, this.viewportManager);
        this.platformManager = new PlatformManager(this.gameStateManager, this.matterWorker, this.gameWorld, this.socketClient, this.viewportManager, this.app);
        this.chat = new Chat(this.gameStateManager.roomState, this.socketClient);
        this.ballManager = new BallManager(this.gameStateManager.roomState, this.matterWorker, this.gameWorld, this.socketClient, this.platformManager, this.app, this.gameStateManager, this.chat, this.viewportManager, this.soundManager, this.renderManager);
        this.uiManager = new UIManager(this.gameStateManager.roomState, this.app, this.gameStateManager, this.socketClient, this.viewportManager, this.gameWorld, this.soundManager, this.renderManager, this.platformManager, this.ballManager);
        this.inputManager = new InputManager(this.app, this.ballManager, this.gameStateManager, this.renderManager, this.gameWorld, this.viewportManager, this.uiManager, this.playerList, this.chat);
        this.playerList = new PlayerList(this.gameStateManager, this.socketClient);

        this.renderManager.signalReady = () => {
            console.log('[LOADING] RenderManager signaling ready');
            window.loadingScreen.setRenderReady();
        };
        this.platformManager.signalReady = () => {
            console.log('[LOADING] PlatformManager signaling ready');
            window.loadingScreen.setPlatformReady();
        };

        new LevelsList(this.gameStateManager.roomState, this.socketClient);
        const windowsManager = new WindowsManager();

        // Hide any existing player list or level selector buttons that might have been created
        setTimeout(() => {
            document.querySelectorAll('button').forEach(btn => {
                const img = btn.querySelector('img');
                if (img && (img.getAttribute('src')?.includes('players') || img.getAttribute('src')?.includes('levels'))) {
                    btn.style.display = 'none';
                }
            });
        }, 100);

        new ParticleEffect(this.app, this.gameWorld);

        // Initialize new settings UI (hides old settings)
        new NewSettingsUI();

        // Initialize recovery UI (for rewarded ads)
        this.recoveryUI = new RecoveryUI();

        // Initialize campaign system
        this.campaignManager = new CampaignManager();
        this.campaignUI = new CampaignUI(this.campaignManager);
        this.campaignLevelCompleteModal = new CampaignLevelCompleteModal(this.campaignManager);

        // Expose to window for debugging and game state manager access
        (window as any).game = this;
        (window as any).campaignLevelCompleteModal = this.campaignLevelCompleteModal;

        // Expose campaign UI for manual triggering (e.g., from a button)
        (window as any).showCampaign = () => {
            this.campaignUI.showLevelSelectWithSettings((level: any, softMultiplayer: boolean) => {
                this.startCampaignLevel(level, softMultiplayer);
            });
        };

        this.app.ticker.add(this.update, this);
        this.app.canvas.style.cursor = 'none';
        console.log('[LOADING] initializeGameComponents completed - app ticker started');

        // Set up cross-references between managers
        this.viewportManager.ballManager = this.ballManager;
        this.renderManager.ballManager = this.ballManager;
        this.uiManager.popupManager.inputManager = this.inputManager;
        this.viewportManager.inputManager = this.inputManager;
        this.platformManager.inputManager = this.inputManager;
        this.platformManager.ballManager = this.ballManager;
        // Expose gold ring/star managers and portal manager to platform manager
        this.platformManager.goldRingManager = this.ballManager.goldRingManager;
        this.platformManager.goldStarManager = this.ballManager.goldStarManager;
        this.platformManager.portalManager = this.ballManager.portalManager;
        this.gameStateManager.platformManager = this.platformManager;
    }

    private setupFPSDisplay() {
        const fpsStyle = new TextStyle({
            fontFamily: 'Arial',
            fontSize: 14,
            fill: 0xFFFFFF,
        });

        this.fpsText = new Text({
            text: 'FPS: 0',
            style: fpsStyle,
        });
        this.fpsText.position.set(window.innerWidth - 100, window.innerHeight - 30);
        this.app.stage.addChild(this.fpsText);
    }

    public startCampaignLevel(level: any, softMultiplayer: boolean = false): void {
        // Set the level seed in room settings
        const settings = JSON.parse(localStorage.getItem('golfUpSettings') || '{}');
        if (!settings.roomSettings) {
            settings.roomSettings = {};
        }
        settings.roomSettings.levelId = level.seed.toString();
        settings.roomSettings.gameType = 'campaign'; // Mark as campaign mode
        settings.roomSettings.softMultiplayer = softMultiplayer;
        localStorage.setItem('golfUpSettings', JSON.stringify(settings));

        // Update campaign manager to current level
        this.campaignManager.currentWorld = level.worldId;
        this.campaignManager.currentLevel = level.levelId;

        // Update roomId to a unique campaign level ID
        const campaignRoomId = `campaign-${level.worldId}-${level.levelId}`;
        this.socketClient.roomId = campaignRoomId;
        window.history.pushState({}, '', `?mode=campaign&level=${level.worldId}-${level.levelId}`);

        // Calculate height for this level (progressive scaling)
        const globalLevel = this.campaignManager.getGlobalLevelNumber(level.worldId, level.levelId);
        const levelHeight = this.campaignManager.getLevelHeight(globalLevel);

        // Create a new room with the seeded level and custom height
        const platformData: any[] = [];
        createPlatformParams(true, this.socketClient, platformData, level.seed, levelHeight);

        // Hide campaign UI
        this.campaignUI.hide();
    }

    public onCampaignLevelComplete(): void {
        const currentLevel = this.campaignManager.getCurrentLevel();
        if (!currentLevel) return;

        // Mark level as complete
        this.campaignManager.completeCurrentLevel();

        // Show completion modal
        this.campaignLevelCompleteModal.show(
            currentLevel,
            () => {
                // Continue to next level
                // First advance to next level
                const hasNext = this.campaignManager.advanceToNextLevel();
                if (hasNext) {
                    const nextLevel = this.campaignManager.getCurrentLevel();
                    if (nextLevel) {
                        const settings = this.campaignManager.getCampaignSettings();
                        this.startCampaignLevel(nextLevel, settings.softMultiplayer);
                    }
                } else {
                    // Campaign complete!
                    alert('Congratulations! You completed the campaign!');
                }
            },
            () => {
                // Show level select
                this.campaignUI.showLevelSelectWithSettings((level, softMultiplayer) => {
                    this.startCampaignLevel(level, softMultiplayer);
                });
            }
        );
    }

    private setupFullscreenHandling() {
        document.addEventListener('keydown', (e) => {
            if (document.activeElement instanceof HTMLInputElement) {
                return;
            }
            if (e.key === 'f' || e.key === 'F' || e.key === 'Escape') {
                toggleFullscreen();
            }
            if (e.key === 'm' || e.key === 'M') {
                this.soundManager.toggleMute();
            }
        });
    }

    private update = (ticker: any) => {
        this.ballManager.update(ticker.deltaTime);
        this.inputManager.update(ticker.deltaTime);
        this.uiManager.update();
        this.renderManager.update(ticker.deltaTime);
        this.platformManager.update(ticker.deltaTime);
        this.viewportManager.update();

        show_fps && this.updateFPS();
    }

    private updateFPS() {
        this.frameCount++;
        const currentTime = performance.now();
        const elapsedTime = currentTime - this.lastTime;

        if (elapsedTime >= 1000) {
            this.fps = Math.round((this.frameCount * 1000) / elapsedTime);
            this.fpsText.text = `Render FPS: ${this.fps} | Physics FPS: ${this.physicsFps}`;
            this.frameCount = 0;
            this.lastTime = currentTime;
        }

        this.fpsText.position.set(window.innerWidth - 250, window.innerHeight - 30);
    }

    private updatePhysicsFPS() {
        this.physicsFrameCount++;
        const currentTime = performance.now();
        const elapsedTime = currentTime - this.lastPhysicsTime;

        if (elapsedTime >= 1000) {
            this.physicsFps = Math.round((this.physicsFrameCount * 1000) / elapsedTime);
            this.physicsFrameCount = 0;
            this.lastPhysicsTime = currentTime;
        }
    }
}

function updateDailyChallengeDisplay() {
    const challenge = DailyChallenge.getTodayChallenge();
    const streak = DailyChallenge.getStreakCount();
    const statusElement = document.getElementById('daily-challenge-status');

    if (!statusElement) return;

    if (challenge.playerBest) {
        const best = challenge.playerBest;
        let statusText = `${best.height.toFixed(0)}ft • ${best.strikes} strikes`;
        if (best.completed) {
            statusText += ' ✅';
        }
        if (streak > 1) {
            statusText += ` 🔥${streak}`;
        }
        statusElement.textContent = statusText;
    } else {
        let statusText = 'New challenge!';
        if (streak > 1) {
            statusText += ` 🔥${streak}`;
        }
        statusElement.textContent = statusText;
    }
}

function showDailyChallengeModal(progress: { height: number; strikes: number; completed: boolean }) {
    const modal = document.getElementById('daily-challenge-modal');
    const resultElement = document.getElementById('challenge-result');
    const statsElement = document.getElementById('challenge-stats');
    const streakElement = document.getElementById('streak-info');

    if (!modal || !resultElement || !statsElement || !streakElement) return;

    // Update result text
    resultElement.textContent = progress.completed ? 'Challenge Completed! 🎉' : 'Challenge Attempted!';

    // Update stats
    statsElement.innerHTML = `
        <div class="text-2xl font-bold text-blue-600 mb-2">${progress.height.toFixed(0)}ft</div>
        <div class="text-sm text-gray-600">Height Reached</div>
        <div class="text-lg font-semibold mt-2">${progress.strikes} strikes</div>
    `;

    // Update streak info
    const streak = DailyChallenge.getStreakCount();
    if (streak > 1) {
        streakElement.textContent = `🔥 ${streak} day streak! Keep it going tomorrow!`;
    } else if (streak === 1) {
        streakElement.textContent = 'First daily challenge completed! Start your streak!';
    } else {
        streakElement.textContent = 'Complete today\'s challenge to start your streak!';
    }

    // Show modal
    modal.classList.remove('hidden');

    // Set up event listeners
    const closeBtn = document.getElementById('close-daily-modal');
    const shareBtn = document.getElementById('share-challenge');
    const playAgainBtn = document.getElementById('play-again-challenge');

    const closeModal = () => modal.classList.add('hidden');

    closeBtn?.addEventListener('click', closeModal);
    playAgainBtn?.addEventListener('click', closeModal);

    shareBtn?.addEventListener('click', () => {
        const shareText = DailyChallenge.generateShareText(progress, streak);
        if (navigator.share) {
            navigator.share({
                title: 'golfUP Daily Challenge',
                text: shareText,
                url: window.location.origin
            }).catch(() => {
                // Fallback to clipboard
                copyToClipboard(shareText);
            });
        } else {
            copyToClipboard(shareText);
        }
        closeModal();
    });

    // Close on background click
    modal.addEventListener('click', (e) => {
        if (e.target === modal) closeModal();
    });
}

function copyToClipboard(text: string) {
    if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(() => {
            // Simple feedback
            const notification = document.createElement('div');
            notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50';
            notification.textContent = 'Copied to clipboard!';
            document.body.appendChild(notification);
            setTimeout(() => notification.remove(), 2000);
        });
    }
}

// Main execution
async function main() {
    const game = new Game();
    await game.init();

    window.addEventListener('beforeunload', () => {
        game.socketClient.leaveRoom();
    });
}

validateAndUpdatePersistedStructure();

// Initialize daily challenge display
updateDailyChallengeDisplay();

// Expose daily challenge functions globally
(window as any).showDailyChallengeModal = showDailyChallengeModal;

// Update daily challenge display when page becomes visible
document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
        updateDailyChallengeDisplay();
    }
});

const playNow = document.getElementById('play-now');
const regularGolf = document.getElementById('regular-golf');
const coinDash = document.getElementById('coindash');
const campaign = document.getElementById('campaign');
const dailyChallenge = document.getElementById('daily-challenge');
const upswing = document.getElementById('upswing');

playNow && playNow.addEventListener('click', startMode.bind(null, GameMode.GolfUP));
regularGolf && regularGolf.addEventListener('click', startMode.bind(null, GameMode.RegularGolf));
coinDash && coinDash.addEventListener('click', startMode.bind(null, GameMode.CoinDash));
upswing && upswing.addEventListener('click', startMode.bind(null, GameMode.snakeup));
campaign && campaign.addEventListener('click', startCampaign);
dailyChallenge && dailyChallenge.addEventListener('click', startDailyChallenge);

function startMode(mode: GameMode) {
    window.loadingScreen = initializeLoadingScreen();

    setTimeout(() => {
        document.getElementById('landing-page')!.style.display = 'none';
        const settings = JSON.parse(localStorage.getItem('golfUpSettings')!);
        settings.roomSettings.gameMode = mode;
        localStorage.setItem('golfUpSettings', JSON.stringify(settings));
        window.history.pushState({}, '', `?room=${mode}`);
        main();
    }, 5);
}

async function startCampaign() {
    window.loadingScreen = initializeLoadingScreen();

    // Set campaign mode in settings BEFORE initializing
    const settings = JSON.parse(localStorage.getItem('golfUpSettings') || '{}');
    if (!settings.roomSettings) {
        settings.roomSettings = {};
    }
    settings.roomSettings.gameType = 'campaign';
    localStorage.setItem('golfUpSettings', JSON.stringify(settings));

    setTimeout(async () => {
        document.getElementById('landing-page')!.style.display = 'none';
        window.history.pushState({}, '', `?mode=campaign`);
        await main();
        // After game initializes, show campaign UI immediately
        // Wait a bit for game to fully initialize
        setTimeout(() => {
            if ((window as any).game?.campaignUI) {
                (window as any).game.campaignUI.showLevelSelectWithSettings((level: any, softMultiplayer: boolean) => {
                    (window as any).game.startCampaignLevel(level, softMultiplayer);
                });
            }
        }, 100);
    }, 5);
}

async function startDailyChallenge() {
    window.loadingScreen = initializeLoadingScreen();

    const challenge = DailyChallenge.getTodayChallenge();

    // Set daily challenge mode in settings BEFORE initializing
    const settings = JSON.parse(localStorage.getItem('golfUpSettings') || '{}');
    if (!settings.roomSettings) {
        settings.roomSettings = {};
    }
    settings.roomSettings.gameType = 'dailyChallenge';
    settings.roomSettings.levelId = challenge.seed.toString();
    settings.roomSettings.dailyChallengeDate = challenge.date;
    localStorage.setItem('golfUpSettings', JSON.stringify(settings));

    setTimeout(async () => {
        document.getElementById('landing-page')!.style.display = 'none';
        window.history.pushState({}, '', `?mode=daily-challenge`);
        await main();
    }, 5);
}

// Unregister service worker
if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (const registration of registrations) {
            registration.unregister();
        }
    });
}